import { cn } from "@/lib/utils"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"

interface Attempt {
  id: string
  attemptNumber: number
  responseStatus: number
  responseBody: string
  error: string
  durationMs: number
  attemptedAt: string
}

interface AttemptsTimelineProps {
  attempts: Attempt[]
}

function getStatusColor(status: number): string {
  if (status >= 200 && status < 300) return "bg-emerald-500"
  if (status >= 400 && status < 500) return "bg-rose-500"
  if (status >= 500) return "bg-amber-500"
  return "bg-zinc-500"
}

function getDurationColor(ms: number): string {
  if (ms < 200) return "bg-emerald-500/15 text-emerald-400"
  if (ms < 1000) return "bg-amber-500/15 text-amber-400"
  return "bg-rose-500/15 text-rose-400"
}

export function AttemptsTimeline({ attempts }: AttemptsTimelineProps) {
  if (attempts.length === 0) {
    return <p className="text-sm text-muted-foreground">No attempts yet</p>
  }

  const sorted = [...attempts].sort((a, b) => b.attemptNumber - a.attemptNumber)

  return (
    <div className="space-y-0">
      {sorted.map((attempt, index) => (
        <div key={attempt.id} className="flex gap-4">
          <div className="flex flex-col items-center">
            <div
              className={cn(
                "w-3 h-3 rounded-full mt-1.5 shrink-0 shadow-[0_0_0.375rem_currentColor]",
                getStatusColor(attempt.responseStatus),
              )}
            />
            {index < sorted.length - 1 && (
              <div className="w-px flex-1 bg-linear-to-b from-border/60 to-border/20 my-1" />
            )}
          </div>

          <div className="pb-6 min-w-0 flex-1 rounded-lg glass-card p-3 mb-2">
            <div className="flex items-center gap-3 mb-1">
              <span className="text-xs font-medium">
                Attempt #{attempt.attemptNumber}
              </span>
              {attempt.responseStatus > 0 && (
                <StatusBadge
                  status={attempt.responseStatus >= 200 && attempt.responseStatus < 300 ? "DELIVERED" : "FAILED"}
                />
              )}
              <span className="text-xs text-muted-foreground tabular-nums">
                {attempt.responseStatus > 0 ? `${attempt.responseStatus}` : "no response"}
              </span>
              <span className={cn(
                "text-2xs tabular-nums rounded-full px-1.5 py-0.5",
                getDurationColor(attempt.durationMs),
              )}>
                {attempt.durationMs}ms
              </span>
            </div>

            <div className="text-xs text-muted-foreground">
              <TimeAgo date={attempt.attemptedAt} />
            </div>

            {attempt.error && (
              <div className="mt-2 p-2 rounded bg-destructive/10 text-xs font-mono text-destructive max-h-20 overflow-auto">
                {attempt.error}
              </div>
            )}

            {attempt.responseBody && (
              <div className="mt-2 p-2 rounded bg-muted/30 text-xs font-mono max-h-20 overflow-auto">
                {attempt.responseBody}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
